import { useEffect, useState } from "react";

import { Dropdown, Modal, Tab, Table, Button, List, Header } from "semantic-ui-react";
import { useIntl } from "react-intl";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";

import { PackageDetailType } from "./sequence/SettingPackage";
import ErrorMessage from "react-lib/component-js/common/ErrorMessage";

const BUTTON_ACTIONS = {
  cancel: "CANCEL_PACKAGE",
};

export const CARD_MOD_PACKAGE_DETAIL = "ModPackageDetail";

type ModPackageDetailProps = {
  setProp: (key: string, value: any, callback?: () => any) => any;
  open: boolean;
  data?: PackageDetailType;
  buttonLoadCheck?: Record<string, any>;
  masterOptions?: Record<string, any[]>;
  errorMessage?: Record<string, any>;
  onClose: () => void;
  onCancel: (reason: string) => any;
};

type TestsPaneProps = {
  items?: PackageDetailType["items"];
};

type InfoPaneProps = {
  recommendation?: string;
};

// ==================== Tab 1: รายการตรวจ ====================
const TestsPane = (props: TestsPaneProps) => {
  const items = (props.items ?? []).filter((item) => item.active);

  return (
    <Tab.Pane attached={false} style={{ border: "none", boxShadow: "none", padding: "16px 0" }}>
      <Table basic="very" unstackable>
        <Table.Header style={{ backgroundColor: "rgba(255, 229, 229, 1)" }}>
          <Table.Row>
            <Table.HeaderCell
              textAlign="center"
              style={{
                fontWeight: 600,
                borderBottom: "1px solid var(--primary-theme-color)",
                padding: "0.5rem 0",
              }}
            >
              รายการตรวจ
            </Table.HeaderCell>
            <Table.HeaderCell
              textAlign="center"
              style={{
                fontWeight: 600,
                borderBottom: "1px solid var(--primary-theme-color)",
                padding: "0.5rem 0",
                width: 80,
              }}
            >
              จำนวน
            </Table.HeaderCell>
            <Table.HeaderCell
              textAlign="center"
              style={{
                fontWeight: 600,
                borderBottom: "1px solid var(--primary-theme-color)",
                padding: "0.5rem 0",
                width: 80,
              }}
            >
              หน่วย
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {items.map((item, index) => (
            <Table.Row key={item.id ?? index} style={{ borderBottom: "none" }}>
              <Table.Cell style={{ borderTop: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "#333" }}>
                  {item.product_name}
                </div>
                {item.product_name_en && (
                  <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                    {item.product_name_en}
                  </div>
                )}
              </Table.Cell>
              <Table.Cell
                textAlign="center"
                style={{
                  fontSize: 14,
                  color: "#333",
                  borderTop: 0,
                }}
              >
                {parseFloat(item.quantity)}
              </Table.Cell>
              <Table.Cell
                textAlign="center"
                style={{
                  fontSize: 14,
                  color: "#333",
                  borderTop: 0,
                }}
              >
                {item.unit_name}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Tab.Pane>
  );
};

// ==================== Tab 2: ข้อมูลเพิ่มเติม ====================
const InfoPane = (props: InfoPaneProps) => (
  <Tab.Pane attached={false} style={{ border: "none", boxShadow: "none", padding: "16px 0" }}>
    <div dangerouslySetInnerHTML={{ __html: props.recommendation || "-" }} />
  </Tab.Pane>
);

// ==================== Main Modal Component ====================
const ModPackageDetail = (props: ModPackageDetailProps) => {
  const intl = useIntl();
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const [cancelReason, setCancelReason] = useState<Partial<{
    open: boolean;
    reason: any;
  }> | null>(null);

  const [cancelPackageOrderOptions, setCancelPackageOrderOptions] = useState<Record<string, any>[]>(
    []
  );

  useEffect(() => {
    setCancelPackageOrderOptions(props.masterOptions?.cancelPackageOrder || []);
  }, [props.masterOptions?.cancelPackageOrder]);

  const handleTabChange = (_e: any, data: any) => {
    setActiveIndex(data.activeIndex ?? 0);
  };

  const handleCancelPackage = () => {
    setCancelReason({ open: true });
  };

  const handleChangeReason = (e: any, v: any) => {
    setCancelReason({ ...cancelReason, reason: v.value });
  };

  const handleCloseModCancel = () => {
    setCancelReason(null);

    props.setProp(`errorMessage.${CARD_MOD_PACKAGE_DETAIL}`, null);
  };

  const handleConfirmCancel = () => {
    props.onCancel?.(cancelReason?.reason);

    props.setProp(`errorMessage.${CARD_MOD_PACKAGE_DETAIL}`, null);
  };

  const handleAddition = (e: any, v: any) => {
    setCancelPackageOrderOptions([
      { key: v.value, text: v.value, value: v.value },
      ...(cancelPackageOrderOptions || []),
    ]);
  };

  const panes = [
    {
      menuItem: "รายการตรวจ",
      render: () => <TestsPane items={props.data?.items} />,
    },
    {
      menuItem: "ข้อมูลเพิ่มเติม",
      render: () => <InfoPane recommendation={props.data?.recommendation} />,
    },
  ];

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      size="small"
      closeIcon
      className="mod-package-detail"
      style={{ borderRadius: 8 }}
    >
      {props.data?.name && (
        <Modal.Header style={{ fontSize: 16, fontWeight: 600 }}>{props.data.name}</Modal.Header>
      )}

      <Modal.Content style={{ padding: "8px 24px 0" }}>
        <Tab
          menu={{
            secondary: true,
            pointing: true,
            style: { borderBottom: "1px solid #eee", marginBottom: 0 },
          }}
          panes={panes}
          activeIndex={activeIndex}
          onTabChange={handleTabChange}
        />
      </Modal.Content>

      <Modal.Actions style={{ borderTop: "1px solid #f0f0f0" }}>
        {props.data?.status !== "PAID" && !props.data?.p_type && (
          <Button color={"red"} style={{ marginRight: "1rem" }} onClick={handleCancelPackage}>
            ยกเลิก Package
          </Button>
        )}
        <Button onClick={props.onClose} color="grey">
          ปิดหน้าต่าง
        </Button>
      </Modal.Actions>

      <ModConfirm
        denyButtonColor="red"
        denyButtonText={intl.formatMessage({ id: "ยกเลิก" })}
        openModal={!!cancelReason?.open}
        size="mini"
        titleColor="blue"
        titleName={intl.formatMessage({ id: "กรุณาระบุเหตุผลการยกเลิก Package" })}
        onCloseWithDimmerClick={handleCloseModCancel}
        onDeny={handleCloseModCancel}
        approveButton={
          <ButtonLoadCheck
            setProp={props.setProp || (() => {})}
            color={"green"}
            name={BUTTON_ACTIONS.cancel}
            paramKey={`${CARD_MOD_PACKAGE_DETAIL}_${BUTTON_ACTIONS.cancel}`}
            size="medium"
            title={intl.formatMessage({ id: "ยืนยัน" })}
            basic
            buttonLoadCheck={
              props.buttonLoadCheck?.[`${CARD_MOD_PACKAGE_DETAIL}_${BUTTON_ACTIONS.cancel}`]
            }
            onClick={handleConfirmCancel}
          />
        }
        content={
          <div style={{ margin: "-2rem 0px -1rem" }}>
            <div style={{ textAlign: "left" }}>
              <div style={{ margin: "20px 0px 10px 0px" }}>
                <ErrorMessage error={props.errorMessage?.[CARD_MOD_PACKAGE_DETAIL]} />
              </div>
              <div style={{ margin: "20px 0px 10px 0px" }}>
                {intl.formatMessage({ id: "สาเหตุ" })}
              </div>
              <Dropdown
                value={cancelReason?.reason || ""}
                allowAdditions
                fluid
                search
                selection
                options={cancelPackageOrderOptions}
                onAddItem={handleAddition}
                onChange={handleChangeReason}
              />
            </div>
          </div>
        }
      />
    </Modal>
  );
};

export default ModPackageDetail;
